import L from 'leaflet';
import 'leaflet.vectorgrid';

import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  TileLayer, useMap
} from 'react-leaflet';
import "react-leaflet-fullscreen/styles.css";
import { Scale } from './scale';

import GLColorScale from 'leaflet.tilelayer.glcolorscale';
import { nanoid } from 'nanoid';
import { API_BASE } from '../api/axios';
import { MapDataContext } from '../context/map-data';
import { LayerDescription } from '../data/layers';
import useColourScale from '../hooks/useColourScale';

function hexToRgbString(hex: string) {
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, function(m, r, g, b) {
    return r + r + g + g + b + b;
  });

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? 
    `rgb(${parseInt(result[1], 16)},${parseInt(result[1], 16)},${parseInt(result[3], 16)})`
  : `rgb(0,0,0)`;
}

const NO_FILL_STYLE = { fill: false } as const;

interface ActiveLayerProps {
  currentLayer: LayerDescription | null
  vectorLayer?: string | null
}

export const ActiveLayer = ({
  currentLayer,
  vectorLayer = null,
}: ActiveLayerProps ) => {
  const [currentValue, setCurrentValue] = useState<number | null>(null)
  const [vectorTileLayer, setVectorTileLayer] = useState<any>(null)
  
  const {
    dataSourceModel,
    showDynamicScaling,
    // setExtremes,
    // extremes,
  } = useContext(MapDataContext);

  const {getColour, lookupTable, useMin, useMax, useReverse, useShowBands, showCustomScale} = useColourScale();
  const map = useMap();

  useEffect(() => {
    if (!map || currentLayer === null) return;
    
    const { dataUrl, minValue, maxValue, colours } = currentLayer
    if (dataUrl && colours) {
      new GLColorScale({
        url: dataUrl,
        onmousemove: ({ pixelValue, ...res }) => {
          // console.log('mousemove', { pixelValue, ...res })
          if (pixelValue === undefined) {
            // Do nothing for `nodata`.
            setCurrentValue(null);
            return;
          } else if (typeof pixelValue === 'number') {
              setCurrentValue(pixelValue);
          } else {
            // Sentinel value: alert with label
          }
        },
        // sentinelValues: [
          // { offset: 0, color: '',  label: 'zero sentinel' },
        //   { offset: 1, color: 'rgb(0, 0, 255)', label: 'one sentinel' },
        // ],
        opacity: 0,   // make invisible as we are only using this for data transfer
        // @ts-ignore
        colorScale: [
          { offset: minValue || 0, color: `${hexToRgbString('#08306b')}` },
          { offset: maxValue || 1, color: `${hexToRgbString('#f7fbff')}` },
        ],
        nodataValue: 0,
      }).addTo(map);
    }
    return () => {
      // Clean up the GLColorScale layer when the component unmounts or dependencies change
      map.eachLayer((layer: any) => {
        if (layer instanceof GLColorScale) {
          map.removeLayer(layer);
        }
      });
    }
  }, [map, currentLayer]);


  const resolvedValueKey = useMemo(() => {
    if (!currentLayer?.valueKey) return null;
    return typeof currentLayer.valueKey === 'string'
      ? currentLayer.valueKey.split('-vector')[0]
      : currentLayer.valueKey;
  }, [currentLayer]);

  const vectorStylesFn = useCallback((properties: any, zoom: number) => {

    if (!currentLayer || !properties || typeof resolvedValueKey !== "string") {
      return NO_FILL_STYLE;
    } 

    let value = NaN 
    if (Object.prototype.hasOwnProperty.call(properties, resolvedValueKey)) {
      value = Number(properties[resolvedValueKey]);
    }
    
    if (Number.isNaN(value)) {
      return NO_FILL_STYLE;
    }

    const fillColor = getColour(value);

    return {
      weight: 0.5,
      color: fillColor,
      fillColor,
      fillOpacity: 1,
      fill: true,
    };

  }, [resolvedValueKey, lookupTable, useMin, useMax, useReverse, useShowBands, showDynamicScaling]);
  
  useEffect(() => {
    if (!map) return;
    if (!showDynamicScaling || !vectorLayer) return;
  
    if (!vectorTileLayer || !map.hasLayer(vectorTileLayer)) {
      // @ts-ignore
      const vectorTileLayer2 = L.vectorGrid.protobuf(vectorLayer, {
        vectorTileLayerStyles: {
          // HACK: bathymetry is the layer name inside the vector tile - accidentally exported instead of default 
          // And cannot be arsed to re-export at the moment
          bathymetry: vectorStylesFn,
        },
        maxZoom: 11,
        interactive: true,
      });
      vectorTileLayer2.addTo(map);
      setVectorTileLayer(vectorTileLayer2);
    } else if (vectorTileLayer && map.hasLayer(vectorTileLayer)) {
      // vectorTileLayer.setStyle({ bathymetry: vectorStylesFn });
      vectorTileLayer.options.vectorTileLayerStyles = {
        bathymetry: vectorStylesFn,
      }
      if (vectorLayer !== vectorTileLayer?._url) {
        vectorTileLayer.setUrl(vectorLayer)
      }

      if (
        !vectorTileLayer._tiles ||
        Object.keys(vectorTileLayer._tiles).length === 0
      ) {
        return;
      }

      vectorTileLayer.redraw()
    }
    return () => {
      if (vectorTileLayer && map.hasLayer(vectorTileLayer)) {
        map.removeLayer(vectorTileLayer);
        setVectorTileLayer(null);
      }
    }
    
  }, [vectorLayer, map, vectorTileLayer, currentLayer?.url, currentLayer?.valueKey,useReverse, useShowBands, useMin, useMax, lookupTable.length, showDynamicScaling]);
  
  if (currentLayer && dataSourceModel) {
    const {attribution, url, title, valueKey, colours, units, minValue, maxValue, displayMaxValue } = currentLayer
    const {model, provider, geography, version} = dataSourceModel
    
    const useUrl = (Array.isArray(url) ? [...url] : [url])

    return useUrl.map(url => 
      <div key={url}>
        {!showCustomScale && <TileLayer
          zIndex={1}
          attribution={attribution}
          url={url || ''}
          maxZoom={11}
          opacity={1}
        />}
        {showCustomScale && <TileLayer
          zIndex={1}
          attribution={attribution}
          url={`${API_BASE}/api/v1/tiles/${model}/${provider}/${geography}/${version}/${valueKey}/${useMin}/${useMax}/${useReverse}/${useShowBands}/{z}/{x}/{y}.png`}
          maxZoom={11}
          opacity={1}
        />}

        {typeof minValue === 'number' && <Scale
          label={`${title}`}
          min={(showCustomScale)? useMin : minValue}
          max={(showCustomScale)? useMax : maxValue}
          units={units}
          currentValue={currentValue}
          colours={colours}
          displayMaxValue={displayMaxValue}
        />}
      </div>
    )
  }

  return null
}


export const GridLayer = ({
  currentLayer,
}: {
  currentLayer: LayerDescription | null
} ) => {
  const [id, setId] = useState<string>(nanoid())
  
  useEffect(() => {
    setId(nanoid())
  }, [currentLayer?.url])

  if (currentLayer) {
    const {url } = currentLayer

    const useUrl = (Array.isArray(url) ? [...url] : [url])

    return useUrl.map(url => 
      <div key={id}>
        <TileLayer
          zIndex={99}
          attribution={''}
          url={url || ''}
          maxZoom={11}
          opacity={0.75}
        />
      </div>
    )
  }

  return null
}


interface CurrentLayerVisibilityProps {
  currentLayer: string | null
  active: string | null
}


